import React, { useEffect, useRef, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AUTH } from "@/constants/testIds";
import PasswordInput from "@/components/PasswordInput";
import Confirm from "@/components/Confirm";
import { toast } from "@/components/Toaster";
import { ShieldCheck, KeyRound, Check, User, Mail, Lock, Save, Bell, AlertTriangle, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Settings() {
  const { user, refresh } = useAuth();

  return (
    <div className="p-8 max-w-3xl space-y-6">
      <div>
        <div className="label-mono text-muted-foreground">— Account / Settings</div>
        <h1 className="mt-3 font-display text-4xl tracking-tighter font-medium">Your account</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Manage your profile, password, and multi-factor authentication.
        </p>
      </div>

      <ProfileSection user={user} onUpdated={refresh} />
      <PasswordSection />
      <MfaSection user={user} onUpdated={refresh} />
      {user?.role === "admin" && <NotificationPrefsSection />}
      <DangerZoneSection user={user} />
    </div>
  );
}

/* -------------------------------- Profile -------------------------------- */

function ProfileSection({ user, onUpdated }) {
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [currentPw, setCurrentPw] = useState("");
  const [msg, setMsg] = useState({ kind: "", text: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setName(user?.name || "");
    setEmail(user?.email || "");
    setCurrentPw("");
  }, [user?.name, user?.email]);

  const nameChanged = name.trim() !== (user?.name || "");
  const emailChanged = email.trim().toLowerCase() !== (user?.email || "");
  const dirty = nameChanged || emailChanged;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg({ kind: "", text: "" });
    try {
      const body = {};
      if (nameChanged) body.name = name.trim();
      if (emailChanged) {
        // P3 hardening — backend requires the current password when
        // rebinding the email. Match the UX we already use for password
        // change: prompt inline, forward as `current_password`.
        if (!currentPw) {
          setMsg({ kind: "error", text: "Current password is required to change your email." });
          setBusy(false);
          return;
        }
        body.email = email.trim().toLowerCase();
        body.current_password = currentPw;
      }
      if (Object.keys(body).length === 0) {
        setMsg({ kind: "info", text: "Nothing to update." });
        return;
      }
      await api.patch("/auth/me", body);
      setCurrentPw("");
      await onUpdated();
      setMsg({ kind: "success", text: "Profile saved." });
    } catch (e) { setMsg({ kind: "error", text: formatError(e) }); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="border border-border bg-card p-6 space-y-5">
      <div className="flex items-center gap-3">
        <User className="w-5 h-5 text-primary" />
        <h3 className="font-display text-xl tracking-tight">Profile</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="label-mono">Full name</label>
          <input
            data-testid="settings-name-input"
            type="text" required value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full px-3 py-2.5 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <div>
          <label className="label-mono">Email address</label>
          <input
            data-testid="settings-email-input"
            type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full px-3 py-2.5 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <div className="mono text-[10px] text-muted-foreground mt-1">
            Changing email will update your sign-in credential. Your session stays active.
          </div>
        </div>
      </div>
      {emailChanged && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
          <label className="label-mono text-amber-600">Confirm current password</label>
          <input
            data-testid="settings-email-current-password"
            type="password"
            required
            autoComplete="current-password"
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
            placeholder="Current password"
            className="mt-1 w-full px-3 py-2.5 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <div className="mono text-[10px] text-muted-foreground mt-1.5">
            Required to change your sign-in email — protects against session-hijack email rebinding.
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="label-mono text-muted-foreground">Role</div>
          <div className="mt-1 text-sm mono uppercase tracking-wider">{user?.role}</div>
        </div>
        <div>
          <div className="label-mono text-muted-foreground">Account created</div>
          <div className="mt-1 text-sm">
            {user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}
          </div>
        </div>
      </div>
      <FlashMsg msg={msg} />
      <button
        data-testid="settings-profile-save"
        type="submit" disabled={busy || !dirty}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition disabled:opacity-50"
      >
        <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save profile"}
      </button>
    </form>
  );
}

/* -------------------------------- Password -------------------------------- */

function PasswordSection() {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [msg, setMsg] = useState({ kind: "", text: "" });
  const [busy, setBusy] = useState(false);

  const mismatch = form.confirm && form.next && form.confirm !== form.next;

  const save = async (e) => {
    e.preventDefault();
    setMsg({ kind: "", text: "" });
    if (form.next !== form.confirm) {
      setMsg({ kind: "error", text: "New passwords do not match." });
      return;
    }
    setBusy(true);
    try {
      await api.patch("/auth/me", {
        current_password: form.current,
        new_password: form.next,
      });
      setForm({ current: "", next: "", confirm: "" });
      setMsg({ kind: "success", text: "Password updated." });
    } catch (e) { setMsg({ kind: "error", text: formatError(e) }); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="border border-border bg-card p-6 space-y-5">
      <div className="flex items-center gap-3">
        <Lock className="w-5 h-5 text-primary" />
        <h3 className="font-display text-xl tracking-tight">Password</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="label-mono">Current password</label>
          <PasswordInput
            testId="settings-current-pw"
            required value={form.current}
            onChange={(e) => setForm({ ...form, current: e.target.value })}
            className="mt-1"
          />
        </div>
        <div>
          <label className="label-mono">New password</label>
          <PasswordInput
            testId="settings-new-pw"
            required minLength={10} value={form.next}
            onChange={(e) => setForm({ ...form, next: e.target.value })}
            className="mt-1"
          />
        </div>
        <div>
          <label className="label-mono">Confirm new password</label>
          <PasswordInput
            testId="settings-confirm-pw"
            required minLength={10} value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            className="mt-1"
          />
          {mismatch && (
            <div className="text-xs text-destructive mt-1">Passwords do not match.</div>
          )}
        </div>
      </div>
      <div className="mono text-[10px] text-muted-foreground">
        Min 10 chars · upper + lower + digit + symbol.
      </div>
      <FlashMsg msg={msg} />
      <button
        data-testid="settings-password-save"
        type="submit" disabled={busy || mismatch || !form.current || !form.next}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition disabled:opacity-50"
      >
        <Save className="w-4 h-4" /> {busy ? "Updating…" : "Update password"}
      </button>
    </form>
  );
}

/* ---------------------------------- MFA ---------------------------------- */

function MfaSection({ user, onUpdated }) {
  const [qr, setQr] = useState(null);
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState({ kind: "", text: "" });
  const [busy, setBusy] = useState(false);

  const startSetup = async () => {
    setBusy(true); setMsg({ kind: "", text: "" });
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setQr(data.qr_code); setSecret(data.secret);
    } catch (e) { setMsg({ kind: "error", text: formatError(e) }); }
    finally { setBusy(false); }
  };

  const enable = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg({ kind: "", text: "" });
    try {
      await api.post("/auth/mfa/enable", { code });
      setQr(null); setSecret(""); setCode("");
      setMsg({ kind: "success", text: "MFA enabled." });
      await onUpdated();
    } catch (e) { setMsg({ kind: "error", text: formatError(e) }); }
    finally { setBusy(false); }
  };

  const [confirmState, setConfirmState] = useState(null);
  const disablePwRef = useRef("");

  const disable = () => {
    disablePwRef.current = "";
    setConfirmState({
      title: "Disable multi-factor authentication?",
      message: (
        <>
          <span className="block mb-3">
            Your account will be protected by password only. You can re-enable
            MFA anytime from this page.
          </span>
          <span className="block text-xs mono uppercase tracking-wider text-muted-foreground mb-1">
            Confirm your current password
          </span>
          <input
            type="password"
            autoFocus
            data-testid="mfa-disable-current-password"
            className="w-full px-3 py-2 border border-border bg-background text-sm rounded"
            onChange={(e) => { disablePwRef.current = e.target.value; }}
            placeholder="Current password"
          />
        </>
      ),
      confirmLabel: "Disable MFA",
      destructive: true,
      onConfirm: async () => {
        const pwd = disablePwRef.current || "";
        if (!pwd) {
          setMsg({ kind: "error", text: "Current password is required." });
          toast.error("Current password is required");
          throw new Error("Password required");
        }
        setBusy(true); setMsg({ kind: "", text: "" });
        try {
          await api.post("/auth/mfa/disable", { current_password: pwd });
          setMsg({ kind: "success", text: "MFA disabled." });
          toast.success("MFA disabled");
          await onUpdated();
        } catch (e) {
          setMsg({ kind: "error", text: formatError(e) });
          toast.error(formatError(e));
          throw e;
        } finally { setBusy(false); disablePwRef.current = ""; }
      },
    });
  };

  return (
    <div className="border border-border bg-card p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-5 h-5 text-primary" />
          <h3 className="font-display text-xl tracking-tight">Multi-factor authentication</h3>
        </div>
        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
          user?.mfa_enabled ? "bg-accent/20 text-accent" : "bg-muted text-muted-foreground"
        }`}>
          {user?.mfa_enabled ? (<><Check className="w-3 h-3 inline mr-1" /> Enabled</>) : "Disabled"}
        </span>
      </div>
      <p className="text-sm text-muted-foreground">
        Add a TOTP code from Google Authenticator, Authy, or 1Password to harden your sign-in.
      </p>

      {!user?.mfa_enabled && !qr && (
        <button
          onClick={startSetup} disabled={busy}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition disabled:opacity-50"
        >
          <KeyRound className="w-4 h-4" /> Start MFA setup
        </button>
      )}

      {qr && (
        <form onSubmit={enable} className="grid md:grid-cols-2 gap-6 pt-2">
          <div>
            <img src={qr} alt="MFA QR" className="w-44 h-44 border border-border bg-white" />
            <div className="mono text-[11px] mt-3 break-all text-muted-foreground">Secret: {secret}</div>
          </div>
          <div>
            <label className="label-mono">Code from authenticator</label>
            <input
              data-testid={AUTH.mfaCode}
              inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="mt-1 w-full px-3 py-3 bg-background border border-border rounded-md font-mono text-center text-2xl tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <button
              data-testid={AUTH.mfaEnable}
              type="submit" disabled={busy}
              className="mt-4 w-full px-4 py-2.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition disabled:opacity-50"
            >Enable MFA</button>
          </div>
        </form>
      )}

      {user?.mfa_enabled && (
        <button
          data-testid={AUTH.mfaDisable}
          onClick={disable} disabled={busy}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-border hover:bg-destructive hover:text-destructive-foreground transition"
        >Disable MFA</button>
      )}

      <FlashMsg msg={msg} />
      <Confirm state={confirmState} onClose={() => setConfirmState(null)} />
    </div>
  );
}

function FlashMsg({ msg }) {
  if (!msg?.text) return null;
  const tone = {
    success: "bg-accent/10 border-accent/30 text-accent",
    error: "bg-destructive/10 border-destructive/30 text-destructive",
    info: "bg-secondary border-border text-muted-foreground",
  }[msg.kind] || "bg-secondary border-border text-muted-foreground";
  return (
    <div className={`text-xs px-3 py-2 rounded-md border ${tone}`}>{msg.text}</div>
  );
}

/* -------------------------- Notification prefs --------------------------- */

function NotificationPrefsSection() {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [kinds, setKinds] = useState({});
  const [emailKinds, setEmailKinds] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/me/notification-prefs")
      .then(({ data }) => {
        if (!alive) return;
        setCategories(data.categories || []);
        setKinds(data.kinds || {});
        setEmailKinds(data.email_kinds || {});
      })
      .catch(() => { if (alive) toast.error("Could not load notification preferences"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const toggle = async (key, channel /* "in_app" | "email" */) => {
    const isEmail = channel === "email";
    const nextInApp = isEmail ? kinds : { ...kinds, [key]: !kinds[key] };
    const nextEmail = isEmail ? { ...emailKinds, [key]: !emailKinds[key] } : emailKinds;
    if (isEmail) setEmailKinds(nextEmail);
    else setKinds(nextInApp);
    setSavingKey(`${key}:${channel}`);
    try {
      const body = isEmail ? { email_kinds: nextEmail } : { kinds: nextInApp };
      const { data } = await api.patch("/me/notification-prefs", body);
      if (data.kinds) setKinds(data.kinds);
      if (data.email_kinds) setEmailKinds(data.email_kinds);
    } catch (e) {
      // rollback
      if (isEmail) setEmailKinds(emailKinds);
      else setKinds(kinds);
      toast.error(formatError(e));
    } finally {
      setSavingKey(null);
    }
  };

  const inAppOn = Object.values(kinds).filter(Boolean).length;
  const emailOn = categories.filter((c) => c.email_capable && emailKinds[c.key]).length;
  const emailCapableCount = categories.filter((c) => c.email_capable).length;

  return (
    <div className="border border-border bg-card p-6 space-y-5">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Bell className="w-5 h-5 text-primary" />
          <div>
            <h3 className="font-display text-xl tracking-tight">Notification preferences</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Two channels per event. <span className="text-foreground">In-app</span> drives
              the bell + toasts. <span className="text-foreground">Email</span> is only
              available for high-context Q&amp;A events to keep your inbox sane.
            </p>
          </div>
        </div>
        <div className="text-right shrink-0 space-y-0.5">
          <div className="mono text-[10px] uppercase tracking-wider text-muted-foreground">
            In-app {inAppOn}/{categories.length}
          </div>
          <div className="mono text-[10px] uppercase tracking-wider text-muted-foreground">
            Email {emailOn}/{emailCapableCount}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading preferences…</div>
      ) : (
        <div className="border border-border rounded-md overflow-hidden">
          <div className="hidden md:grid grid-cols-[1fr_auto_auto] gap-6 items-center px-4 py-2 bg-secondary/40 label-mono text-muted-foreground border-b border-border">
            <span>Event</span>
            <span className="text-center w-20">In-app</span>
            <span className="text-center w-20">Email</span>
          </div>
          <div className="divide-y divide-border">
            {categories.map((c) => {
              const on = !!kinds[c.key];
              const eon = !!emailKinds[c.key];
              const savingIn = savingKey === `${c.key}:in_app`;
              const savingEm = savingKey === `${c.key}:email`;
              return (
                <div
                  key={c.key}
                  data-testid={`notif-pref-${c.key}`}
                  className="grid grid-cols-[1fr_auto_auto] gap-6 items-center px-4 py-3 hover:bg-secondary/30 transition"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{c.label}</div>
                    <div className="text-xs text-muted-foreground truncate">{c.hint}</div>
                  </div>
                  <ChannelSwitch
                    on={on}
                    onClick={() => toggle(c.key, "in_app")}
                    disabled={savingIn}
                    testid={`notif-pref-${c.key}-in-app`}
                  />
                  {c.email_capable ? (
                    <ChannelSwitch
                      on={eon}
                      onClick={() => toggle(c.key, "email")}
                      disabled={savingEm}
                      testid={`notif-pref-${c.key}-email`}
                    />
                  ) : (
                    <div
                      title="Email not supported for this category"
                      className="w-20 text-center mono text-[10px] uppercase tracking-wider text-muted-foreground/60"
                    >
                      —
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mono text-[10px] text-muted-foreground border-t border-dashed border-border pt-3 leading-relaxed">
        — Critical events like room deletion or invite removal bypass these toggles and always notify all admins.
        Email alerts use the same address as your sign-in account.
      </div>
    </div>
  );
}

function ChannelSwitch({ on, onClick, disabled, testid }) {
  return (
    <div className="w-20 flex justify-center">
      <button
        type="button"
        data-testid={testid}
        role="switch"
        aria-checked={on}
        onClick={onClick}
        disabled={disabled}
        className={`relative inline-flex w-10 h-6 rounded-full transition shrink-0 ${
          on ? "bg-primary" : "bg-secondary border border-border"
        } disabled:opacity-60`}
      >
        <span className={`absolute top-0.5 ${on ? "left-[18px]" : "left-0.5"} w-5 h-5 rounded-full bg-background transition-all`} />
      </button>
    </div>
  );
}


/* ------------------------------ Danger zone ------------------------------ */

/**
 * Self-service account deletion. Sectioned visually as a "danger zone" so
 * it never sits adjacent to benign account edits — destructive UI must
 * read as destructive at a glance:
 *
 *   • Outer card uses a destructive accent border + tinted background.
 *   • Warning icon + bold consequence summary above the CTA.
 *   • Primary CTA is muted (ghost) at rest — clicking opens a confirm
 *     dialog where the user must type their email verbatim. Only then
 *     does the dialog's red "Delete account" button enable.
 *   • Successful delete signs out + redirects to /, with a toast.
 *   • Backend guard rails (sole owner / sole admin / seed admin) bubble
 *     up via formatError → toast so the user knows exactly which path to
 *     take next.
 */
function DangerZoneSection({ user }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmInput, setConfirmInput] = useState("");
  const [busy, setBusy] = useState(false);
  const email = (user?.email || "").toLowerCase();
  const matches = confirmInput.trim().toLowerCase() === email && !!email;

  const submit = async () => {
    if (!matches || busy) return;
    setBusy(true);
    try {
      await api.delete("/auth/me");
      toast.success("Your account has been deleted.");
      // Hard reload so AuthContext, axios interceptors, and any cached
      // socket connections are all torn down cleanly.
      window.location.assign("/");
    } catch (e) {
      toast.error(formatError(e));
      setBusy(false);
    }
  };

  // Keep the confirm field empty whenever the dialog re-opens — prevents
  // a stale "ready to delete" state after a previous cancel.
  useEffect(() => {
    if (!open) setConfirmInput("");
  }, [open]);

  return (
    <section
      data-testid="settings-danger-zone"
      className="border border-destructive/40 bg-destructive/5 rounded-lg p-6"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-4 h-4 text-destructive" />
        </div>
        <div className="flex-1">
          <div className="label-mono text-destructive">— Danger zone</div>
          <h2 className="mt-1 font-display text-xl tracking-tight font-medium">Delete your account</h2>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
            Permanently removes your account and every per-user record —
            memberships, document permissions, notifications, activity
            trail and pending access requests. <strong className="text-foreground">This cannot be undone.</strong>
          </p>
          <ul className="mt-3 text-xs text-muted-foreground space-y-1 list-disc pl-5">
            <li>Documents you uploaded remain in their data rooms — they're owned by the room, not by you.</li>
            <li>Other users won't be notified.</li>
            <li>If you're the only App Owner or Admin, you must promote someone else first.</li>
          </ul>
          <button
            data-testid="settings-delete-account-btn"
            onClick={() => setOpen(true)}
            className="mt-5 inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md border border-destructive/50 text-destructive hover:bg-destructive/10 transition"
          >
            <Trash2 className="w-4 h-4" />
            Delete account
          </button>
        </div>
      </div>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 bg-background/80 backdrop-blur flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="bg-background border border-border rounded-lg max-w-md w-full p-6">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-destructive" />
              </div>
              <h3 className="font-display text-xl tracking-tight">Confirm deletion</h3>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              To confirm, type your account email <span className="mono text-foreground">{email}</span> below.
              This action is permanent.
            </p>
            <input
              data-testid="settings-delete-confirm-input"
              autoFocus
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              placeholder={email}
              className="mt-4 w-full px-3 py-2 bg-secondary rounded-md border border-border focus:border-destructive outline-none mono text-sm"
            />
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                data-testid="settings-delete-cancel-btn"
                onClick={() => setOpen(false)}
                disabled={busy}
                className="px-3 py-2 text-sm rounded-md border border-border hover:bg-secondary transition"
              >
                Cancel
              </button>
              <button
                data-testid="settings-delete-confirm-btn"
                onClick={submit}
                disabled={!matches || busy}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-destructive text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-destructive/90 transition"
              >
                <Trash2 className="w-3.5 h-3.5" />
                {busy ? "Deleting…" : "Delete account"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
